import type { Action, FighterInit, RoundEvent } from '@cs-shot/battle-sim';
import { fighterLayout } from '@cs-shot/battle-sim';
import { weaponKeyOf, stanceOf, resolveSkin } from '../spriteLayout';
import type { Fighter, Scene } from './types';
import type { PublicFighter } from '../game/useRushMidMatch';
import { now } from './battleConfig';

export interface BattleEngineOpts {
  replay: boolean;
  getYouId: () => string | null;
  getTarget: () => string | null;
  /** the local player's action committed THIS round (null before they pick) — drives
   *  their own name colour + the action icon by their name. */
  getChosenAction: () => Action | null;
  onYouHit: () => void;
  onYouDeath: () => void;
  /** локализованные плашки промаха для canvas («мимо / уворот / ослеплён») */
  getMissLabels: () => { miss: string; evade: string; blind: string };
}

export interface TimelineItem { at: number; run: (n: number) => void; }
/** a grenade in flight: the whole EVENT rides along so the blast can commit it to the
 *  cursor at LANDING time (committing at throw time would land the damage early) */
export interface PendingBlast { at: number; cx: number; cy: number; ev: Extract<RoundEvent, { type: 'grenade' }>; }
/** a smoke canister in flight: at `at` (landing) it wakes the target's screen entry */
export interface PendingSmoke { at: number; target: string; }
/** a standing smoke screen: fixed world anchor + the last covered round. `bornAt` is stamped
 *  when the canister LANDS — the cloud is invisible before it, blooms in from it and stands
 *  through its whole coverage: ONE owner from landing to expiry, so there is no separate
 *  transient bloom to hand off to (that handoff dipped to nothing at the end of the throw
 *  round, then popped back). `fadeAt` starts the drift-away. */
export interface SmokeScreenFx { until: number; x: number; y: number; bornAt?: number; fadeAt?: number; }
/** a standing flashbang glare on a caught fighter: round-anchored exactly like the smoke screen.
 *  `bornAt` is the flash pop (its canister's detonation) — the glare is bright at it, then holds
 *  STEADY through its whole coverage: ONE owner from pop to expiry, so the wash never bleeds into
 *  the next round and dies mid-round on the replay clock. `fadeAt` starts the ease-out. */
export interface BlindGlowFx { until: number; bornAt: number; fadeAt?: number; }
export interface PendingShot { at: number; fromX: number; fromY: number; toX: number; toY: number; key: string; angle: number; shooter: string; target: string; }

/** All mutable playback state for one match, held in one record so the schedule /
 *  actor / motion / pump helpers can share it by reference instead of closing over
 *  a dozen loose variables. */
export interface PlaybackState {
  inits: FighterInit[];
  prevFinal: Record<string, PublicFighter> | null;
  /** The authoritative per-fighter state DURING the replay, advanced one event at a time
   *  through battle-sim's applyRoundEvent (the same code the history fold uses). The
   *  renderer assigns hp/armor/kit from here — it never does damage arithmetic itself. */
  cursor: Record<string, PublicFighter> | null;
  timeline: TimelineItem[];
  pendingBlasts: PendingBlast[];
  pendingSmokes: PendingSmoke[];
  /** extra burst muzzle flashes/tracers — visual only, no damage */
  pendingShots: PendingShot[];
  /** shooters whose knife target is ALSO knifing them back (they meet in the middle) */
  mutualKnifers: Set<string>;
  /** each shooter → the enemy they engage this round (first shot's target), so every barrel
   *  can track its own target for the whole replay — not just at the instant it fires. */
  roundTargets: Map<string, string>;
  /** smoked fighter → its standing screen. The engine's shield lasts SMOKE_SHIELD_ROUNDS (2)
   *  — the throw round AND the next — so the cloud must keep standing through the follow-up
   *  round too, or its voided hits read as random misses. The anchor is the fighter's SETTLED
   *  battle spot captured at schedule time — never the live x, or a knife lunge drags the
   *  cloud across the map with the body. Once coverage ends `fadeAt` is stamped and the cloud
   *  drifts away instead of blinking off. */
  smokeScreens: Map<string, SmokeScreenFx>;
  /** flashed fighter → its standing glare. The engine's blind lasts FLASH_BLIND_ROUNDS (2) — the
   *  flash round AND the next — same coverage arithmetic as the smoke shield, so the glare keeps
   *  standing through the follow-up round too instead of dying on the replay clock mid-round. Once
   *  coverage ends `fadeAt` is stamped and the glare eases off; drawBlindGlare deletes it once
   *  fully faded. */
  blindGlows: Map<string, BlindGlowFx>;
  activeAt: number;
  replayDur: number;
  activeRound: number;
  /** time the match-start entrance began (0 = not started) */
  enterAt: number;
}

export const createPlaybackState = (): PlaybackState => ({
  inits: [],
  prevFinal: null,
  cursor: null,
  timeline: [],
  pendingBlasts: [],
  pendingSmokes: [],
  pendingShots: [],
  mutualKnifers: new Set(),
  roundTargets: new Map(),
  smokeScreens: new Map(),
  blindGlows: new Map(),
  activeAt: 0,
  replayDur: 0,
  activeRound: -2,
  enterAt: 0,
});

export const buildFighters = (sc: Scene, inits: FighterInit[], snap: Record<string, PublicFighter>): void => {
  // match-start intro: spawn each fighter at its own side of the MAP/field boundary
  // (world x = 0 for T on the left, sc.W for CT on the right — NOT off in the black
  // letterbox), then advanceFighters eases x → targetX so they run in across the field.
  // The run cycle plays automatically (walk keeps |targetX − x| large).
  // Combat baseline (hp/armor/kit) comes from the round-0 fighter snapshot — the seat
  // init carries only identity + weapon.
  const teamSize = { t: 0, ct: 0 };
  for (const fi of inits) teamSize[fi.team]++;
  sc.fighters = inits.map((fi) => {
    const dir = fi.team === 't' ? 1 : -1;
    const fromX = dir > 0 ? 0 : sc.W;
    const { y, targetX } = fighterLayout(fi.team, fi.index, teamSize[fi.team]);
    const s = snap[fi.id];
    return {
      id: fi.id, team: fi.team, name: fi.name,
      dir,
      spawnX: targetX, x: fromX, y, targetX, enterFromX: fromX,
      weapon: fi.weapon, weaponKey: weaponKeyOf(fi.team, fi.weapon),
      weaponSkin: fi.weaponSkins?.[weaponKeyOf(fi.team, fi.weapon)],
      stance: stanceOf(fi.weapon), combatStance: 'attack', roundAction: null, skin: resolveSkin(fi.team, fi.id, fi.skin),
      afk: !!s.afk,
      maxHp: s.maxHp, hp: s.maxHp, shield: 0, armor: s.armor,
      hasGrenade: s.hasGrenade, hasSmoke: s.hasSmoke, hasMedkit: s.hasMedkit, hasFlash: s.hasFlash,
      dead: false, deadAt: 0, recoil: 0,
      aim: fi.team === 't' ? 0 : Math.PI, phase: 0,
    } satisfies Fighter;
  });
  sc.byId = {};
  for (const f of sc.fighters) sc.byId[f.id] = f;
};

/** Pull each fighter's authoritative HP/armor/stance/kit/dead from a snapshot. Used both
 *  to seed a round from the previous round's end state and to reconcile once its replay ends. */
export const syncTo = (sc: Scene, snap: Record<string, PublicFighter>): void => {
  for (const f of sc.fighters) {
    const s = snap[f.id];
    if (!s) continue;
    f.hp = s.hp;
    f.shield = 0; // adrenaline never survives its round — the authoritative state carries none
    f.armor = s.armor;
    f.combatStance = s.stance;
    f.afk = !!s.afk;
    f.hasGrenade = s.hasGrenade;
    f.hasSmoke = s.hasSmoke;
    f.hasMedkit = s.hasMedkit;
    f.hasFlash = s.hasFlash;
    if (!s.alive && !f.dead) { f.dead = true; f.deadAt = now(); }
  }
};

/** The enemy a fighter's barrel + ring track this frame. During a round's replay everyone
 *  (you included) follows the enemy they ACTUALLY shot (roundTargets), so barrel, ring and
 *  bullets never point at three different enemies. Only in the decision phase does YOUR aim
 *  follow your chosen ring target (the next enemy you'll shoot). */
export const aimTargetOf = (
  id: string | null, youId: string | null, replaying: boolean, state: PlaybackState, opts: BattleEngineOpts,
): string | null => {
  if (id === youId && !replaying) return opts.getTarget();
  return id ? state.roundTargets.get(id) ?? null : null;
};
